// =======================
// src/estilos/global.js
// =======================

// Aplicar somente em fontSize: soma o ajuste escolhido ao tamanho padrão.
export function ajustarTamanhoFonte(tamanho, variavel = "--sear-ajuste-fonte") {
  const tamanhoBase = typeof tamanho === "number" ? `${tamanho}px` : tamanho;
  return `calc(${tamanhoBase} + var(${variavel}, 0px))`;
}

// Estilos compartilhados acompanham o Aa dos quatro perfis.
export function ajustarTamanhoFonteAdmin(tamanho) {
  const tamanhoBase = typeof tamanho === "number" ? `${tamanho}px` : tamanho;
  return `calc(${tamanhoBase} + var(--sear-ajuste-fonte-admin, 0px) + var(--sear-ajuste-fonte-sre, 0px) + var(--sear-ajuste-fonte-analista, 0px) + var(--sear-ajuste-fonte-escola, 0px))`;
}

// Textos e componentes compartilhados por SRE, Analista e Escola.
export function ajustarTamanhoFonteSre(tamanho) {
  const tamanhoBase = typeof tamanho === "number" ? `${tamanho}px` : tamanho;
  return `calc(${tamanhoBase} + var(--sear-ajuste-fonte-sre, 0px) + var(--sear-ajuste-fonte-analista, 0px) + var(--sear-ajuste-fonte-escola, 0px))`;
}

// Textos próprios da análise; os componentes reutilizados pela Escola conservam a base.
export function ajustarTamanhoFonteAnalista(tamanho) {
  return ajustarTamanhoFonte(tamanho, "--sear-ajuste-fonte-analista");
}

// Textos da Escola; nos demais perfis a variável permanece zerada.
export function ajustarTamanhoFonteEscola(tamanho) {
  return ajustarTamanhoFonte(tamanho, "--sear-ajuste-fonte-escola");
}

// Tamanhos originais das variantes do MUI usadas pelos temas atuais.
// Os títulos das páginas administrativas continuam definidos separadamente.
const tamanhosTipografiaMui = {
  h1: "6rem",
  h2: "3.75rem",
  h3: "3rem",
  h4: "2.125rem",
  h5: "1.5rem",
  h6: "1.25rem",
  subtitle1: "1rem",
  subtitle2: "0.875rem",
  body1: "1rem",
  body2: "0.875rem",
  button: "0.875rem",
  caption: "0.75rem",
  overline: "0.75rem",
};

// Fonte e pesos compartilhados com os temas do MUI.
export const tipografiaGlobal = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  ...Object.fromEntries(
    Object.entries(tamanhosTipografiaMui).map(([variante, tamanho]) => [
      variante,
      { fontSize: ajustarTamanhoFonte(tamanho) },
    ]),
  ),
};

// Tamanhos do padrão Normal, em pixels.
export const tamanhosGlobais = {
  tituloPagina: 22,
  tituloSecao: 18,
  textoPadrao: 16,
  textoAuxiliar: 14,
  menu: 16,
  botao: 16,
  tabela: 18,
  aviso: 14,
  status: 12,
  dica: 12,
  rodape: 14,
  rotuloFlutuante: 12,
  iconePrincipal: 24,
  iconeSecao: 22,
  iconeMenu: 20,
  iconeAcao: 20,
  iconeTabela: 18,
  iconeConteudoTabela: 16,
};

// Cores por categoria visual, compartilhadas entre os quatro perfis.
// Cores de situações e ações específicas continuam definidas pelos componentes.
export const coresTextosGlobais = {
  tituloPagina: "var(--sear-azul, #0767d8)",
  tituloSecao: "var(--sear-azul, #0767d8)",
  cabecalhoTabela: "var(--sear-azul, #2261a5)",
  conteudoTabela: "var(--sear-azul, #365d90)",
  link: "var(--sear-azul, #0767d8)",
};

// Tamanhos do Painel, da lista de SREs e da lista de Cidades.
// Nos títulos em rem, a fonte raiz de 16px resulta em 21px e 24px.
// Aplicar somente em fontSize para manter os demais estilos de cada elemento.
export const tamanhosPaginasAdministrativas = {
  tituloPagina: {
    xs: ajustarTamanhoFonte("1.3125rem"), // 21px
    md: ajustarTamanhoFonte("1.5rem"), // 24px
  },
  tituloSecao: ajustarTamanhoFonte("17px"),
  cabecalhoTabela: ajustarTamanhoFonte("15px"),
  conteudoTabela: ajustarTamanhoFonte("13px"),
};

// Tamanhos dos controles, em rem, equivalentes a pixels inteiros na raiz de 16px.
const tamanhoControleLista = ajustarTamanhoFonte("0.8125rem"); // 13px
const tamanhoContagemLista = ajustarTamanhoFonte("0.8125rem"); // 13px

export const tamanhosControlesGlobais = {
  descricao: ajustarTamanhoFonte("0.875rem"), // 14px
  botao: tamanhoControleLista,
  campoBusca: tamanhoControleLista,
  rotulo: ajustarTamanhoFonte("0.75rem"), // 12px
  seletor: tamanhoContagemLista,
  contador: tamanhoContagemLista,
  paginacao: tamanhoControleLista,
  reticencias: ajustarTamanhoFonte("0.9375rem"), // 15px
};

// Padrão tipográfico dos modais de cadastro e edição.
// Valores base em rem; a preferência soma um ajuste em pixels.
// As equivalências em pixels consideram uma fonte raiz de 16px.
// Aplicar em fontSize para preservar fonte, peso, cores e espaçamentos.
export const tamanhosModaisGlobais = {
  tituloModal: ajustarTamanhoFonte("1.5rem"), // 24px
  tituloSecao: ajustarTamanhoFonte("1.0625rem"), // 17px
  descricao: ajustarTamanhoFonte("0.875rem"), // 14px
  aba: ajustarTamanhoFonte("0.875rem"), // 14px
  rotulo: ajustarTamanhoFonte("0.875rem"), // 14px
  campo: ajustarTamanhoFonte("0.9375rem"), // 15px
  opcaoSelecao: ajustarTamanhoFonte("0.9375rem"), // 15px
  textoAuxiliar: ajustarTamanhoFonte("0.875rem"), // 14px
  botao: ajustarTamanhoFonte("0.875rem"), // 14px
};

// As dicas de hover compartilham o azul das ações do catálogo em todos os perfis.
export const componentesInteracaoGlobais = {
  MuiTableCell: {
    styleOverrides: {
      root: ({ ownerState, theme }) => {
        if (ownerState.variant === "head") return {
          fontSize: tamanhosPaginasAdministrativas.cabecalhoTabela,
          ...(theme.palette.mode !== "dark" && { color: coresTextosGlobais.cabecalhoTabela }),
        };
        if (ownerState.variant === "body") return {
          fontSize: tamanhosPaginasAdministrativas.conteudoTabela,
          ...(theme.palette.mode !== "dark" && { color: coresTextosGlobais.conteudoTabela }),
        };
        return {};
      },
    },
  },
  MuiTableSortLabel: {
    styleOverrides: {
      root: ({ theme }) => theme.palette.mode === "dark" ? {} : {
        color: coresTextosGlobais.cabecalhoTabela,
        "&.Mui-active": { color: coresTextosGlobais.cabecalhoTabela },
        "& .MuiTableSortLabel-icon": { color: coresTextosGlobais.cabecalhoTabela },
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      input: {
        "&:focus::placeholder": {
          opacity: 0,
          transition: "none",
        },
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: "var(--sear-superficie-hover, #0767d8)",
        color: "var(--sear-texto, #ffffff)",
      },
      arrow: { color: "var(--sear-superficie-hover, #0767d8)" },
    },
  },
};

// Aplicado somente ao tema do Admin. Alguns componentes do MUI possuem
// tamanhos próprios e não acompanham apenas as variantes de typography.
export const componentesTipografiaAdmin = {
  MuiTypography: {
    styleOverrides: {
      h4: {
        fontSize: tamanhosPaginasAdministrativas.tituloPagina.xs,
        "@media (min-width:900px)": {
          fontSize: tamanhosPaginasAdministrativas.tituloPagina.md,
        },
      },
      h6: { fontSize: tamanhosPaginasAdministrativas.tituloSecao },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: { fontSize: tamanhosModaisGlobais.botao },
      sizeSmall: { fontSize: tamanhosModaisGlobais.botao },
      sizeLarge: { fontSize: tamanhosModaisGlobais.botao },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: { fontSize: tamanhosModaisGlobais.campo },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: { fontSize: tamanhosModaisGlobais.rotulo },
      // O MUI reduz o rótulo flutuante para 75% com transform: scale(0.75).
      shrink: { fontSize: `calc(${tamanhosModaisGlobais.rotulo} / 0.75)` },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: { fontSize: tamanhosModaisGlobais.textoAuxiliar },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: { fontSize: tamanhosModaisGlobais.opcaoSelecao },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: { fontSize: tamanhosModaisGlobais.tituloModal },
    },
  },
  MuiDialogContentText: {
    styleOverrides: {
      root: { fontSize: tamanhosModaisGlobais.descricao },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: { fontSize: tamanhosPaginasAdministrativas.cabecalhoTabela },
      body: { fontSize: tamanhosPaginasAdministrativas.conteudoTabela },
      footer: { fontSize: tamanhosPaginasAdministrativas.conteudoTabela },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: { fontSize: tamanhosPaginasAdministrativas.conteudoTabela },
      avatar: { fontSize: ajustarTamanhoFonte("0.75rem") },
      avatarSmall: { fontSize: ajustarTamanhoFonte("0.625rem") },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: { fontSize: ajustarTamanhoFonte("0.6875rem") },
      touch: { fontSize: ajustarTamanhoFonte("0.875rem") },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: { fontSize: ajustarTamanhoFonte("1.25rem") },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: { fontSize: tamanhosModaisGlobais.aba },
      wrapped: { fontSize: tamanhosModaisGlobais.aba },
    },
  },
};

// Valores originais dos componentes MUI com tamanho próprio, além de typography.
// SRE e Analista reutilizam este tema: a base em 100% é preservada nos dois acessos.
export const componentesTipografiaSre = {
  MuiButton: {
    styleOverrides: {
      sizeSmall: { fontSize: ajustarTamanhoFonteSre("0.8125rem") },
      sizeLarge: { fontSize: ajustarTamanhoFonteSre("0.9375rem") },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: { fontSize: ajustarTamanhoFonteSre("0.8125rem") },
      avatar: { fontSize: ajustarTamanhoFonteSre("0.75rem") },
      avatarSmall: { fontSize: ajustarTamanhoFonteSre("0.625rem") },
    },
  },
  MuiListSubheader: {
    styleOverrides: {
      root: { fontSize: ajustarTamanhoFonteSre("0.875rem") },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      footer: { fontSize: ajustarTamanhoFonteSre("0.75rem") },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: { fontSize: ajustarTamanhoFonteSre("0.6875rem") },
      touch: { fontSize: ajustarTamanhoFonteSre("0.875rem") },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: { fontSize: ajustarTamanhoFonteSre("1.25rem") },
    },
  },
  MuiBadge: {
    styleOverrides: {
      badge: { fontSize: ajustarTamanhoFonteSre("0.75rem") },
    },
  },
  MuiPaginationItem: {
    styleOverrides: {
      sizeLarge: { fontSize: ajustarTamanhoFonteSre("0.9375rem") },
    },
  },
};

// A Escola usa as medidas do Admin para títulos e controles.
// O Aa continua somando o ajuste escolhido, sem ampliar o restante do layout.
export const componentesTipografiaEscola = {
  ...componentesTipografiaSre,
  ...componentesTipografiaAdmin,
  MuiTypography: {
    styleOverrides: {
      ...componentesTipografiaAdmin.MuiTypography.styleOverrides,
      h5: { fontSize: tamanhosPaginasAdministrativas.tituloSecao },
      body1: { fontSize: tamanhosControlesGlobais.descricao },
    },
  },
};

// Valores proporcionais ao tamanho da fonte.
export const alturasLinhaGlobais = {
  titulo: 1.3,
  texto: 1.5,
};

// Usamos unidades explícitas para manter estes valores em pixels no sx.
export const espacamentosGlobais = {
  tituloDescricao: "8px",
  descricaoCampos: "16px",
  secoes: "24px",
};

function criarTexto(
  tamanho,
  peso = tipografiaGlobal.fontWeightRegular,
  alturaLinha = alturasLinhaGlobais.texto,
) {
  return {
    fontFamily: tipografiaGlobal.fontFamily,
    fontSize: ajustarTamanhoFonte(tamanho),
    fontWeight: peso,
    lineHeight: alturaLinha,
  };
}

// Estilos de texto para aplicar nas páginas e componentes.
export const textosGlobais = {
  tituloPagina: criarTexto(
    tamanhosGlobais.tituloPagina,
    tipografiaGlobal.fontWeightBold,
    alturasLinhaGlobais.titulo,
  ),

  tituloSecao: criarTexto(
    tamanhosGlobais.tituloSecao,
    tipografiaGlobal.fontWeightBold,
    alturasLinhaGlobais.titulo,
  ),

  textoPadrao: criarTexto(tamanhosGlobais.textoPadrao),

  textoAuxiliar: criarTexto(tamanhosGlobais.textoAuxiliar),

  menu: criarTexto(tamanhosGlobais.menu, tipografiaGlobal.fontWeightMedium),

  botao: {
    ...criarTexto(tamanhosGlobais.botao, tipografiaGlobal.fontWeightMedium),
    textTransform: "none",
  },

  tabela: criarTexto(tamanhosGlobais.tabela),

  cabecalhoTabela: criarTexto(
    tamanhosGlobais.tabela,
    tipografiaGlobal.fontWeightBold,
  ),

  aviso: criarTexto(tamanhosGlobais.aviso),

  status: criarTexto(tamanhosGlobais.status),

  dica: criarTexto(tamanhosGlobais.dica),

  rodape: criarTexto(tamanhosGlobais.rodape),
};

// Elementos que reutilizam categorias já definidas.
export const textosComplementaresGlobais = {
  tituloModal: textosGlobais.tituloSecao,
  aba: textosGlobais.botao,
  filtro: textosGlobais.botao,
  paginacao: textosGlobais.botao,
  campo: textosGlobais.textoPadrao,
  opcaoSelecao: textosGlobais.textoPadrao,
  erroCampo: textosGlobais.textoAuxiliar,
  contadorPequeno: textosGlobais.status,
};

// Estilos dos ícones.
export const iconesGlobais = {
  principal: {
    fontSize: `${tamanhosGlobais.iconePrincipal}px`,
  },

  secao: {
    fontSize: `${tamanhosGlobais.iconeSecao}px`,
  },

  menu: {
    fontSize: `${tamanhosGlobais.iconeMenu}px`,
  },

  acao: {
    fontSize: `${tamanhosGlobais.iconeAcao}px`,
  },

  tabela: {
    fontSize: `${tamanhosGlobais.iconeTabela}px`,
  },

  conteudoTabela: {
    fontSize: `${tamanhosGlobais.iconeConteudoTabela}px`,
  },
};

// Aplicar no contêiner de cada bloco.
export const blocosGlobais = {
  descricao: {
    mt: espacamentosGlobais.tituloDescricao,
  },

  campos: {
    mt: espacamentosGlobais.descricaoCampos,
  },

  secoes: {
    gap: espacamentosGlobais.secoes,
  },
};

// Aplicar no contêiner externo das janelas que usam o contorno azul superior.
export const bordaSuperiorModalGlobal = {
  borderTop: "4px solid var(--sear-borda-forte, #087cf0)",
};
